import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import type { tableBody_nativeIssueTable_issueResults$key } from '@atlassian/jira-relay/src/__generated__/tableBody_nativeIssueTable_issueResults.graphql.ts';
import type { tableBody_nativeIssueTable_project$key } from '@atlassian/jira-relay/src/__generated__/tableBody_nativeIssueTable_project.graphql.ts';
import { fg } from '@atlassian/jira-feature-gating';
import type { Column, TableInjectedCellProps } from '../../../common/types.tsx';
import { ChildRows } from '../../rows/child-rows/index.tsx';
import { Rows } from '../../rows/index.tsx';
import { RowContextProvider, type RenderChildRows } from '../../rows/row-context/index.tsx';

export type Props = {
	columns: Column[];
	tableInjectedProps: TableInjectedCellProps;
	issueResults: tableBody_nativeIssueTable_issueResults$key;
	project: tableBody_nativeIssueTable_project$key | null;
	isGroupingSupported?: boolean;
};

const TableBody = ({ issueResults, project, isGroupingSupported, ...tableBodyProps }: Props) => {
	const issueResultsData = useFragment<tableBody_nativeIssueTable_issueResults$key>(
		graphql`
			fragment tableBody_nativeIssueTable_issueResults on JiraIssueConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isReparentingEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
			) {
				__id
				...rows_nativeIssueTable
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isReparentingEnabled: $isReparentingEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
					)
			}
		`,
		issueResults,
	);

	const projectData = useFragment<tableBody_nativeIssueTable_project$key>(
		graphql`
			fragment tableBody_nativeIssueTable_project on JiraProject {
				key
			}
		`,
		project,
	);

	const renderChildRows: RenderChildRows = useCallback(
		(props) => (
			<ChildRows
				projectKey={projectData?.key}
				isGroupingSupported={isGroupingSupported}
				{...props}
			/>
		),
		[projectData?.key, isGroupingSupported],
	);

	return (
		<StyledTableBody>
			<RowContextProvider {...tableBodyProps} renderChildRows={renderChildRows}>
				<Rows
					issueResults={issueResultsData}
					parentItemId="root"
					itemsConnectionId={issueResultsData.__id}
					{...(fg('jsc_list_reparenting') ? { projectKey: projectData?.key } : {})}
				/>
			</RowContextProvider>
		</StyledTableBody>
	);
};

export default TableBody;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTableBody = styled.tbody({
	borderBottom: 'none',
	// Ensure position is correctly calculated when an inline edit popup is opened that overflows the table body
	overflowX: 'hidden',
});
